import React from 'react'
import * as zarr from 'zarrita'
import type { Dataset, ControlsProps } from './types'
import { BandSelector } from '../components/SharedControls'

const S2_L2A_ZARR = 'S2C_MSIL2A_20260716T100601_N0512_R022_T32UQU_20260716T152417.zarr'
const PROXY_BASE = `/eopf-examples/${S2_L2A_ZARR}`

// 3 multiscale levels: r2 (120m), r6 (360m), r12 (720m)
const LEVELS = ['r2', 'r6', 'r12']

// Custom store that injects the multiscales layout into root zarr.json
class EopfS2L2aStore {
  async get(key: string): Promise<Uint8Array | undefined> {
    const k = key.startsWith('/') ? key.slice(1) : key
    const resp = await fetch(`${PROXY_BASE}/${k}`)
    if (!resp.ok) return undefined
    if (k === 'zarr.json') {
      const meta = await resp.json()
      meta.attributes = {
        ...meta.attributes,
        multiscales: {
          layout: LEVELS.map((l) => ({ asset: `multiscales/${l}` })),
        },
      }
      return new TextEncoder().encode(JSON.stringify(meta))
    }
    return new Uint8Array(await resp.arrayBuffer())
  }

  async getRange(
    key: string,
    range: { offset?: number; length?: number; suffixLength?: number },
    options?: { signal?: AbortSignal }
  ): Promise<Uint8Array | undefined> {
    const k = key.startsWith('/') ? key.slice(1) : key
    const rangeHeader =
      range.suffixLength !== undefined
        ? `bytes=-${range.suffixLength}`
        : `bytes=${range.offset}-${(range.offset ?? 0) + (range.length ?? 0) - 1}`
    const resp = await fetch(`${PROXY_BASE}/${k}`, {
      headers: { Range: rangeHeader },
      signal: options?.signal,
    })
    if (!resp.ok) return undefined
    return new Uint8Array(await resp.arrayBuffer())
  }
}

// L2A bands at r2 level (BOA reflectance)
const BANDS = ['b02', 'b03', 'b04', 'b05', 'b06', 'b07', 'b8a', 'b11', 'b12']
type Band = typeof BANDS[number]

type S2L2aState = { band: Band }

const Controls = ({ state, setState }: ControlsProps<S2L2aState>) => (
  <BandSelector
    value={state.band}
    options={BANDS}
    onChange={(band) => setState({ band })}
    label="Band"
  />
)

const eopfS2L2a: Dataset<S2L2aState> = {
  id: 'eopf-s2-l2a',
  tags: ['sentinel-2', 'optical', 'austria'],
  source: PROXY_BASE,
  variable: 'b04',
  clim: [0, 0.4],
  colormap: 'greens',
  zarrVersion: 3,
  // Native CRS: EPSG:32632 (UTM zone 32N)
  proj4: '+proj=utm +zone=32 +datum=WGS84 +units=m +no_defs',
  spatialDimensions: { lat: 'y', lon: 'x' },
  bounds: [699960, 5290200, 809760, 5400000],
  bounds4326: [11.6670, 47.6909, 13.2083, 48.7209],
  latIsAscending: false,
  fillValue: 0,
  center: [12.44, 48.21],
  zoom: 9,
  info: 'Sentinel-2 L2A MSI',
  subtitle: 'Austria · UTM 32N · 120 m coarsest · 2026-07-16',
  sourceInfo:
    'Sentinel-2C MSI L2A product (EOPF zarr v3) — BOA reflectance, tile T32UQU, acquired 2026-07-16.',
  store: Promise.resolve(new EopfS2L2aStore() as zarr.Readable),
  defaultState: { band: 'b04' },
  Controls,
  buildLayerProps: (state) => ({
    variable: state.band,
    selector: {},
  }),
}

export default eopfS2L2a
